import { Box, Dialog, IconButton, Typography, useMediaQuery, type Theme } from '@mui/material';
import { CircleAlert, Search, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { Avatar, Badge, Button, Icon, Kbd, Skeleton, visuallyHidden } from '../kit';
import type { RenderedNavSection } from '../navigation';
import {
  accountOption,
  announceResults,
  describeAccount,
  filterPages,
  MIN_SEARCH_LENGTH,
  pageOption,
  pagesOf,
  searchTermOf,
  type AccountSearchState,
  type PaletteOption,
} from './paletteModel';
import { modifierLabel } from './shortcut';
import { useAccountSearch } from './useAccountSearch';

/**
 * Palette de commandes (maquette P03, BO-21) : un exploitant arrive avec un seul
 * indice — une adresse, un nom de page — et la palette l'y amene au clavier, de
 * n'importe quel ecran.
 *
 * **Elle navigue, elle n'agit pas** : aucune ligne ne modifie une donnee. Deux
 * groupes seulement, ceux que le serveur sait deja servir :
 *
 * - « Comptes » : `GET /admin/users?search=` (voir `useAccountSearch`) ;
 * - « Aller à » : les entrees rendues par la barre laterale, filtrees ici.
 *
 * Motif ARIA « combobox a liste » : la saisie garde le focus **en permanence**
 * (`role="combobox"`), la ligne active est designee par `aria-activedescendant`
 * et se deplace aux fleches. Le piege de focus, le rendu du focus a l'element
 * d'origine et Echap viennent du `Dialog` de MUI (le `Dialog` du kit impose un
 * en-tete titre, que la palette n'a pas). **La liste ne contient que des
 * options** : chargement, vide et erreur sont des blocs freres, jamais des
 * enfants du `listbox`.
 */

const optionId = (baseId: string, key: string): string => `${baseId}-${key}`;

const GROUP_LABEL_SX = {
  px: 2.5,
  pt: 1.5,
  pb: 0.75,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'tokens.textMuted',
} as const;

interface OptionRowProps {
  readonly id: string;
  readonly active: boolean;
  readonly lead: ReactNode;
  readonly title: string;
  readonly detail: string;
  readonly badges?: ReactNode;
  readonly onActivate: (newTab: boolean) => void;
  readonly onHover: () => void;
}

const OptionRow = ({ id, active, lead, title, detail, badges, onActivate, onHover }: OptionRowProps): ReactElement => (
  <Box
    id={id}
    role="option"
    aria-selected={active}
    onMouseMove={onHover}
    onClick={(event) => {
      onActivate(event.metaKey || event.ctrlKey);
    }}
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      columnGap: 1.5,
      rowGap: 0.75,
      px: 2.5,
      py: 1.25,
      cursor: 'pointer',
      bgcolor: active ? 'tokens.primarySoft' : 'transparent',
    }}
  >
    {lead}
    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, lineHeight: '20px', color: active ? 'tokens.primary' : 'tokens.textStrong' }}>
        {title}
      </Typography>
      {/* Sous 900 px la ligne passe a la ligne plutot que de couper « Aucun profil bébé ». */}
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: '16px',
          color: 'tokens.textMuted',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: { xs: 'normal', md: 'nowrap' },
        }}
      >
        {detail}
      </Typography>
    </Box>
    {badges === undefined ? null : (
      // Sous 900 px les pastilles passent sous le texte, alignees dessus : une
      // adresse longue ne se laisse pas tronquer a 150 px par deux badges.
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flex: { xs: '0 0 100%', md: '0 0 auto' }, pl: { xs: '44px', md: 0 } }}>
        {badges}
      </Box>
    )}
    {active ? (
      <Box aria-hidden sx={{ display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', gap: 0.75, color: 'tokens.primary', fontSize: 12, fontWeight: 700 }}>
        Ouvrir
        <Kbd>↵</Kbd>
      </Box>
    ) : null}
  </Box>
);

/** Ligne d'attente : la forme d'un compte, rien d'annonce (la region vivante parle). */
const SkeletonRow = (): ReactElement => (
  <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.25 }}>
    <Skeleton width={32} height={32} radius={16} />
    <Box sx={{ flex: 1, display: 'grid', gap: 0.75 }}>
      <Skeleton width="55%" height={12} />
      <Skeleton width="35%" height={10} />
    </Box>
  </Box>
);

/** Etat du groupe « Comptes » quand il n'a aucune option a montrer. */
const AccountsStatus = ({
  search,
  term,
  onRetry,
}: {
  readonly search: AccountSearchState;
  readonly term: string;
  readonly onRetry: () => void;
}): ReactElement | null => {
  if (search.kind === 'loading') {
    return (
      <div>
        <Typography component="div" sx={GROUP_LABEL_SX}>
          Comptes
        </Typography>
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (search.kind === 'error') {
    return (
      <div>
        <Typography component="div" sx={GROUP_LABEL_SX}>
          Comptes
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 2.5, py: 1 }}>
          <Box sx={{ display: 'inline-flex', color: 'tokens.criticalText' }}>
            <Icon icon={CircleAlert} size={18} />
          </Box>
          <Typography sx={{ flex: '1 1 200px', fontSize: 14 }}>{search.message}</Typography>
          <Button variant="ghost" size="S" onClick={onRetry}>
            Réessayer
          </Button>
        </Box>
      </div>
    );
  }

  if (search.kind === 'ready' && search.users.length === 0) {
    return (
      <div>
        <Typography component="div" sx={GROUP_LABEL_SX}>
          Comptes
        </Typography>
        <Typography sx={{ px: 2.5, py: 1, fontSize: 14, color: 'tokens.textMuted' }}>
          Aucun compte pour « {term} ».
        </Typography>
      </div>
    );
  }

  return null;
};

/** Ligne d'aide sous la saisie : ce qui manque pour chercher, ou pourquoi on ne le peut pas. */
const helperOf = (search: AccountSearchState): string | null => {
  switch (search.kind) {
    case 'idle':
      return `Tapez au moins ${String(MIN_SEARCH_LENGTH)} caractères pour chercher un compte`;
    case 'forbidden':
      return 'Votre rôle ne permet pas cette recherche.';
    case 'ready':
      return search.users.length < search.total
        ? `${String(search.users.length)} comptes affichés sur ${new Intl.NumberFormat('fr-FR').format(search.total)} : précisez la saisie.`
        : null;
    default:
      return null;
  }
};

interface PaletteContentProps {
  readonly sections: readonly RenderedNavSection[];
  readonly onClose: () => void;
  /** Le 403 est retenu **hors** du contenu : il survit a la fermeture, les resultats non. */
  readonly forbidden: boolean;
  readonly onForbidden: () => void;
}

/**
 * Contenu de la palette. Il vit **avec l'ouverture** : le `Dialog` le demonte a
 * la fin de sa transition de sortie et il est remonte a neuf (`key` de session),
 * si bien que la saisie, la ligne active et les comptes lus n'existent plus apres
 * la fermeture — une adresse ou un etat de compte vu hier ne reparait jamais
 * comme une donnee d'aujourd'hui.
 */
const PaletteContent = ({ sections, onClose, forbidden, onForbidden }: PaletteContentProps): ReactElement => {
  const navigate = useNavigate();
  const baseId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const narrow = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [query, setQuery] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const { state: search, retry } = useAccountSearch(query, forbidden, onForbidden);

  const pages = useMemo(() => pagesOf(sections), [sections]);
  const matchingPages = useMemo(() => filterPages(pages, query), [pages, query]);
  const accounts = useMemo(() => (search.kind === 'ready' ? search.users : []), [search]);
  const accountOptions = useMemo(() => accounts.map(accountOption), [accounts]);
  const pageOptions = useMemo(() => matchingPages.map(pageOption), [matchingPages]);
  const options = useMemo<readonly PaletteOption[]>(
    () => [...accountOptions, ...pageOptions],
    [accountOptions, pageOptions],
  );

  // La ligne active est suivie par sa **clef**, pas par sa position : quand les
  // comptes arrivent au-dessus des pages, la ligne que l'on visait ne change pas
  // sous la touche Entree. Sans clef connue, c'est la premiere.
  const active = options.find((option) => option.key === activeKey) ?? options[0] ?? null;
  const activeId = active === null ? undefined : optionId(baseId, active.key);

  useEffect(() => {
    if (activeId === undefined) {
      return;
    }

    const row = document.getElementById(activeId);

    // `scrollIntoView` manque sous jsdom.
    if (row !== null && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' });
    }
  }, [activeId]);

  const close = (): void => {
    onClose();
  };

  const activate = (option: PaletteOption, newTab: boolean): void => {
    if (newTab) {
      // Le routeur vit dans le fragment : l'URL d'un autre onglet est la meme page, un autre fragment.
      const { origin, pathname, search: queryString } = window.location;

      window.open(`${origin}${pathname}${queryString}#${option.path}`, '_blank', 'noopener,noreferrer');

      return;
    }

    close();
    void navigate(option.path);
  };

  const move = (delta: 1 | -1): void => {
    if (options.length === 0) {
      return;
    }

    const current = active === null ? 0 : options.findIndex((option) => option.key === active.key);
    const next = options[(current + delta + options.length) % options.length];

    if (next !== undefined) {
      setActiveKey(next.key);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    // Une validation d'IME (accents, saisie composee) ne doit rien ouvrir.
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter' && active !== null) {
      event.preventDefault();
      activate(active, event.metaKey || event.ctrlKey);
    }
  };

  const term = searchTermOf(query);
  const helper = helperOf(search);
  const modifier = modifierLabel();

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, borderBottom: 1, borderColor: 'tokens.border' }}>
        <Box sx={{ display: 'inline-flex', color: 'tokens.accentPinkStrong' }}>
          <Icon icon={Search} size={22} />
        </Box>
        <Box
          component="input"
          ref={inputRef}
          autoFocus
          type="text"
          role="combobox"
          aria-label="Rechercher un compte ou aller à une page"
          aria-autocomplete="list"
          aria-expanded={options.length > 0}
          aria-controls={options.length > 0 ? `${baseId}-liste` : undefined}
          aria-activedescendant={activeId}
          aria-describedby={helper === null ? undefined : `${baseId}-aide`}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={narrow ? 'Compte ou page…' : 'Rechercher un compte, aller à une page…'}
          value={query}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setQuery(event.target.value);
            setActiveKey(null);
          }}
          onKeyDown={onKeyDown}
          sx={{
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            bgcolor: 'transparent',
            color: 'tokens.textStrong',
            font: 'inherit',
            fontSize: 18,
            fontWeight: 600,
            '&::placeholder': { color: 'tokens.textMuted', fontWeight: 400 },
          }}
        />
        {narrow ? (
          // Au toucher, pas d'Echap : une croix visible ferme la palette.
          <IconButton
            aria-label="Fermer la palette"
            onClick={() => {
              close();
            }}
            sx={{ color: 'tokens.textMuted' }}
          >
            <Icon icon={X} size={18} />
          </IconButton>
        ) : null}
      </Box>

      {helper === null ? null : (
        <Typography id={`${baseId}-aide`} sx={{ px: 2.5, pt: 1.25, fontSize: 13, color: 'tokens.textMuted' }}>
          {helper}
        </Typography>
      )}

      {/* Region vivante : le nombre de resultats, annonce a chaque changement. */}
      <Box role="status" sx={visuallyHidden}>
        {announceResults(search, matchingPages.length)}
      </Box>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, maxHeight: 'min(420px, 55vh)', overflowY: 'auto', pb: 1 }}>
        <AccountsStatus
          search={search}
          term={term}
          onRetry={() => {
            retry();
            inputRef.current?.focus();
          }}
        />
        {options.length === 0 ? null : (
          <Box
            id={`${baseId}-liste`}
            role="listbox"
            aria-label="Résultats"
            // Garder le focus dans la saisie : un clic sur une ligne ne le lui prend pas.
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            {accountOptions.length === 0 ? null : (
              <div role="group" aria-labelledby={`${baseId}-comptes`}>
                <Typography id={`${baseId}-comptes`} component="div" aria-hidden sx={GROUP_LABEL_SX}>
                  Comptes
                </Typography>
                {accountOptions.map((option, index) => (
                    <OptionRow
                      key={option.key}
                      id={optionId(baseId, option.key)}
                      active={active?.key === option.key}
                      lead={<Avatar name={option.user.email} tone={index % 2 === 0 ? 'pink' : 'mint'} />}
                      title={option.user.email}
                      detail={describeAccount(option.user)}
                      badges={
                        <>
                          {option.user.anonymized ? <Badge label="Anonymisé" tone="warning" /> : null}
                          <Badge
                            label={option.user.emailVerifiedAt === null ? 'Non vérifiée' : 'Vérifiée'}
                            tone={option.user.emailVerifiedAt === null ? 'neutral' : 'success'}
                          />
                        </>
                      }
                      onHover={() => {
                        setActiveKey(option.key);
                      }}
                      onActivate={(newTab) => {
                        activate(option, newTab);
                      }}
                    />
                  ))}
              </div>
            )}
            {pageOptions.length === 0 ? null : (
              <div role="group" aria-labelledby={`${baseId}-pages`}>
                <Typography id={`${baseId}-pages`} component="div" aria-hidden sx={GROUP_LABEL_SX}>
                  Aller à
                </Typography>
                {pageOptions.map((option) => (
                    <OptionRow
                      key={option.key}
                      id={optionId(baseId, option.key)}
                      active={active?.key === option.key}
                      lead={
                        <Box
                          aria-hidden
                          sx={{ width: 32, height: 32, borderRadius: '8px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'tokens.neutralSoft', color: 'tokens.textMuted' }}
                        >
                          <Icon icon={option.page.icon} size={16} />
                        </Box>
                      }
                      title={option.page.label}
                      detail={option.page.sectionLabel}
                      onHover={() => {
                        setActiveKey(option.key);
                      }}
                      onActivate={(newTab) => {
                        activate(option, newTab);
                      }}
                    />
                  ))}
              </div>
            )}
          </Box>
        )}
        {pageOptions.length === 0 && accountOptions.length === 0 && term !== '' ? (
          <Typography sx={{ px: 2.5, pt: 1.5, fontSize: 14, color: 'tokens.textMuted' }}>
            Aucune page ne correspond à « {term} ».
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          px: 2.5,
          py: 1.5,
          borderTop: 1,
          borderColor: 'tokens.border',
          bgcolor: 'tokens.surfaceMuted',
          fontSize: 12,
          color: 'tokens.textMuted',
        }}
      >
        {/* Les gestes au clavier n'ont de sens que sur un clavier. */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            naviguer
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Kbd>↵</Kbd>
            ouvrir
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Kbd>{modifier}</Kbd>
            <Kbd>↵</Kbd>
            nouvel onglet
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Kbd>Échap</Kbd>
            fermer
          </Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        {/* Exact : chaque recherche de compte ecrit une ligne d'audit (ADR 0032). */}
        <span>Recherche journalisée</span>
      </Box>
    </>
  );
};

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Ce que la barre laterale rend pour le role courant. */
  readonly sections: readonly RenderedNavSection[];
}

export const CommandPalette = ({ open, onClose, sections }: CommandPaletteProps): ReactElement => {
  const [forbidden, setForbidden] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  const [session, setSession] = useState(0);

  // Chaque ouverture est une session neuve : si la palette est rouverte pendant
  // le fondu de sortie, le contenu n'a pas encore ete demonte et serait repris.
  if (open !== wasOpen) {
    setWasOpen(open);

    if (open) {
      setSession((previous) => previous + 1);
    }
  }

  const markForbidden = useCallback(() => {
    setForbidden(true);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      slotProps={{
        paper: {
          'aria-label': 'Palette de commandes',
          sx: {
            m: 0,
            mt: { xs: 2, md: '96px' },
            width: 'min(680px, calc(100vw - 32px))',
            maxHeight: 'calc(100% - 32px)',
            borderRadius: '20px',
            overflow: 'hidden',
          },
        },
      }}
    >
      <PaletteContent
        key={session}
        sections={sections}
        onClose={onClose}
        forbidden={forbidden}
        onForbidden={markForbidden}
      />
    </Dialog>
  );
};
